<template>
  <div class="container">
    <!-- Header con breadcrumb y controles -->

    <div class="level">
      <div class="level-left">
        <div class="level-item">
          <h1 class="title">{{ $t("appraisal map") }}</h1>
        </div>
      </div>
      <div class="level-right">
        <div class="level-item">
          <div class="field is-grouped">
            <div class="control">
              <button
                class="button is-info"
                @click="refresh_map_data"
                :class="{ 'is-loading': loading_data }"
              >
                <span class="icon"><i class="fa fa-refresh"></i></span>
                <span>{{ $t("refresh") }}</span>
              </button>
            </div>
            <div class="control">
              <div class="select">
                <select v-model="map_view_type" @change="update_map_view">
                  <option value="all">{{ $t("all appraisals") }}</option>
                  <option value="active">{{ $t("active only") }}</option>
                  <option value="recent">{{ $t("recent only") }}</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Search Location Section -->
    <div class="box mb-4">
      <div class="columns">
        <div class="column is-half">
          <div class="field">
            <label class="label">{{ $t("search location") }}</label>
            <div class="field has-addons">
              <div class="control has-icons-left is-expanded">
                <input
                  class="input"
                  type="text"
                  v-model="search_query"
                  @keyup.enter="search_location()"
                  :placeholder="$t('enter address or place name')"
                />
                <span class="icon is-left">
                  <i class="fa fa-search"></i>
                </span>
              </div>
              <div class="control">
                <button
                  class="button is-primary"
                  @click="search_location()"
                  :class="{ 'is-loading': searching_location }"
                  :disabled="!search_query || searching_location"
                >
                  <span class="icon">
                    <i class="fa fa-search"></i>
                  </span>
                  <span>{{ $t("search") }}</span>
                </button>
              </div>
            </div>
            <p class="help">{{ $t("search help text") }}</p>
          </div>
        </div>
        <div class="column">
          <div class="field">
            <label class="label">{{ $t("map controls") }}</label>
            <div class="field is-grouped">
              <div class="control">
                <button
                  class="button is-light"
                  @click="center_map_guatemala()"
                  :data-tooltip="$t('center in guatemala')"
                  data-tooltip-position="top"
                >
                  <span class="icon"><i class="fa fa-home"></i></span>
                  <!-- <span>{{ $t("center in guatemala") }}</span> -->
                </button>
              </div>
              <div class="control">
                <button
                  class="button is-light"
                  @click="fit_all_markers()"
                  :disabled="map_data.length === 0"
                  :data-tooltip="$t('fit all')"
                >
                  <span class="icon"><i class="fa fa-expand"></i></span>
                  <!-- <span>{{ $t("fit all") }}</span> -->
                </button>
              </div>
              <div class="control">
                <button
                  class="button is-warning"
                  @click="force_map_refresh()"
                  :data-tooltip="$t('refresh map')"
                >
                  <span class="icon"><i class="fa fa-refresh"></i></span>
                  <!-- <span>{{ $t("refresh map") }}</span> -->
                </button>
              </div>
              <div class="control">
                <button
                  class="button is-info"
                  @click="switch_tile_provider()"
                  :data-tooltip="$t('switch tiles')"
                >
                  <span class="icon"><i class="fa fa-globe"></i></span>
                  <!-- <span>{{ $t("switch tiles") }}</span> -->
                </button>
              </div>
              <div class="control">
                <button class="button is-success" @click="load_map_data()">
                  <span class="icon"><i class="fa fa-download"></i></span>
                  <!-- <span>Test Load Data</span> -->
                </button>
              </div>
              <div class="control">
                <button class="button is-danger" @click="debug_map_state()">
                  <span class="icon"><i class="fa fa-bug"></i></span>
                  <!-- <span>Debug</span> -->
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Estadísticas rápidas -->
    <div class="columns mb-4">
      <div class="column is-3">
        <div class="box has-text-centered">
          <p class="heading">{{ $t("total properties") }}</p>
          <p class="title is-4 has-text-primary">{{ map_statistics.total }}</p>
        </div>
      </div>
      <div class="column is-3">
        <div class="box has-text-centered">
          <p class="heading">{{ $t("average price per sqm") }}</p>
          <p class="title is-4 has-text-success">
            {{ format_currency(map_statistics.avg_price) }}
          </p>
        </div>
      </div>
      <div class="column is-3">
        <div class="box has-text-centered">
          <p class="heading">{{ $t("highest value") }}</p>
          <p class="title is-4 has-text-warning">
            {{ format_currency(map_statistics.max_price) }}
          </p>
        </div>
      </div>
      <div class="column is-3">
        <div class="box has-text-centered">
          <p class="heading">{{ $t("lowest value") }}</p>
          <p class="title is-4 has-text-info">
            {{ format_currency(map_statistics.min_price) }}
          </p>
        </div>
      </div>
    </div>

    <!-- Controles del mapa -->
    <div class="box mb-4">
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <div class="field">
              <div class="control">
                <label class="checkbox">
                  <input
                    type="checkbox"
                    v-model="enable_clustering"
                    @change="toggle_clustering"
                  />
                  {{ $t("enable marker clustering") }}
                </label>
              </div>
            </div>
          </div>
          <div class="level-item">
            <div class="field">
              <div class="control">
                <label class="checkbox">
                  <input
                    type="checkbox"
                    v-model="show_price_labels"
                    @change="toggle_price_labels"
                  />
                  {{ $t("show price labels") }}
                </label>
              </div>
            </div>
          </div>
        </div>
        <div class="level-right">
          <div class="level-item">
            <div class="field is-grouped">
              <div class="control">
                <button class="button is-small" @click="zoom_to_fit">
                  <span class="icon"><i class="fa fa-search"></i></span>
                  <span>{{ $t("zoom to fit") }}</span>
                </button>
              </div>
              <div class="control">
                <button class="button is-small" @click="reset_map_view">
                  <span class="icon"><i class="fa fa-home"></i></span>
                  <span>{{ $t("reset view") }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Mapa principal -->
    <div class="box">
      <div
        id="main-map"
        style="height: 70vh; width: 100%"
        ref="mapContainer"
      ></div>
    </div>

    <!-- Panel de información seleccionada -->
    <div class="box" v-if="selected_property">
      <h4 class="title is-5">{{ $t("selected property") }}</h4>
      <div class="columns">
        <div class="column is-8">
          <div class="content">
            <p>
              <strong>{{ $t("property address") }}:</strong>
              {{ selected_property.property_address }}
            </p>
            <p>
              <strong>{{ $t("property type") }}:</strong>
              {{
                selected_property.property_type
                  ? selected_property.property_type.name
                  : $t("not specified")
              }}
            </p>
            <p>
              <strong>{{ $t("price per sqm") }}:</strong>
              {{ format_currency(selected_property.price_per_sqm) }}
            </p>
            <p>
              <strong>{{ $t("total area") }}:</strong>
              {{ selected_property.total_area }} m²
            </p>
            <p>
              <strong>{{ $t("total value") }}:</strong>
              {{
                format_currency(
                  selected_property.price_per_sqm * selected_property.total_area
                )
              }}
            </p>
          </div>
        </div>
        <div class="column is-4">
          <div class="field is-grouped">
            <div class="control">
              <button
                class="button is-primary"
                @click="view_property_details(selected_property.uuid)"
              >
                <span class="icon"><i class="fa fa-eye"></i></span>
                <span>{{ $t("view details") }}</span>
              </button>
            </div>
            <div class="control">
              <button
                class="button is-warning"
                @click="edit_property(selected_property.uuid)"
              >
                <span class="icon"><i class="fa fa-edit"></i></span>
                <span>{{ $t("edit") }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading overlay -->
    <div class="modal" :class="{ 'is-active': loading_data }">
      <div class="modal-background"></div>
      <div class="modal-content">
        <div class="box has-text-centered">
          <div class="is-size-4 mb-3">
            <i class="fa fa-spinner fa-spin"></i>
          </div>
          <p>{{ $t("loading map data") }}...</p>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <appraisal-detail-modal
      ref="appraisal_detail_modal"
      @edit="edit_property"
    />
    <appraisal-modal ref="appraisal_modal" @success="on_appraisal_updated" />
  </div>
</template>

<script>
import {
  get,
  format_currency,
  success_message,
  error_message,
  warning_message,
} from "@/utils";
import AppraisalDetailModal from "./appraisal_datatable/appraisal_detail_modal/Index.vue";
import AppraisalModal from "./appraisal_datatable/appraisal_modal/Index.vue";
import L from "leaflet";

// Import Leaflet CSS
import "leaflet/dist/leaflet.css";

// Fix Leaflet default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

export default {
  name: "AppraisalMap",
  components: {
    AppraisalDetailModal,
    AppraisalModal,
  },
  data() {
    return {
      debug_mode: true, // Temporarily enabled to debug the data loading issue
      map: null,
      markers: [],
      marker_cluster: null,
      currentTileLayer: null,
      appraisals: [],
      selected_property: null,
      loading_data: false,
      map_view_type: "all",
      enable_clustering: false, // Disabled for debugging
      show_price_labels: false,
      map_statistics: {
        total: 0,
        avg_price: 0,
        max_price: 0,
        min_price: 0,
      },
      search_query: "",
      searching_location: false,
      search_timeout: null,
      map_init_retries: 0,
    };
  },
  computed: {
    map_data() {
      return this.get_filtered_appraisals();
    },
  },
  mounted() {
    // Debug information
    this.debug_log("🔍 [Map.vue] Component mounted");
    this.debug_log("🔍 [Map.vue] Leaflet available:", typeof L !== "undefined");
    this.debug_log(
      "🔍 [Map.vue] Container exists:",
      !!document.getElementById("main-map")
    );

    // Use $nextTick and a longer delay to ensure DOM is fully ready
    this.$nextTick(() => {
      setTimeout(() => {
        this.init_map();
      }, 500); // Increased delay to 500ms
    });
  },
  watch: {
    search_query(newVal) {
      // Clear previous timeout
      if (this.search_timeout) {
        clearTimeout(this.search_timeout);
      }

      // Only search if there's meaningful input (at least 3 characters)
      if (newVal && newVal.length >= 3) {
        this.search_timeout = setTimeout(() => {
          this.search_location();
        }, 1000); // Wait 1 second after user stops typing
      }
    },
  },
  beforeDestroy() {
    this.debug_log("🔄 [Map.vue] Component being destroyed");
    // Clear any pending timeouts
    if (this.search_timeout) {
      clearTimeout(this.search_timeout);
    }
    this.destroy_map();
  },
  methods: {
    // Helper method for conditional debug logging
    debug_log(...args) {
      if (this.debug_mode) {
        console.log(...args);
      }
    },

    // Helper method to check if map is ready for operations
    is_map_ready() {
      return this.map && this.map._container && this.map._loaded;
    },

    init_map() {
      this.debug_log("🗺️ [Map.vue] Starting map initialization...");

      // Debug state
      this.debug_map_state();

      // Check Leaflet availability
      if (typeof L === "undefined") {
        console.error("❌ [Map.vue] Leaflet not available");
        return;
      }

      // Get container
      const container = document.getElementById("main-map");
      if (!container) {
        console.error("❌ [Map.vue] Container #main-map not found");
        // Retry after delay
        setTimeout(() => {
          this.init_map();
        }, 1000);
        return;
      }

      // Ensure container has dimensions
      container.style.height = "70vh";
      container.style.width = "100%";
      container.style.display = "block";

      this.debug_log("✅ [Map.vue] Container found and sized");

      try {
        // Clean up existing map
        if (this.map) {
          this.debug_log("🔄 [Map.vue] Removing existing map");
          this.map.remove();
          this.map = null;
        }

        // Simple map creation
        this.debug_log("🏗️ [Map.vue] Creating new map instance");
        this.map = L.map(container, {
          center: [14.6349, -90.5069],
          zoom: 8,
        });

        this.debug_log("✅ [Map.vue] Map object created:", !!this.map);

        // Add basic tile layer
        this.debug_log("🌍 [Map.vue] Adding tile layer");
        this.currentTileLayer = L.tileLayer(
          "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
          {
            attribution: "© OpenStreetMap contributors",
          }
        );

        this.currentTileLayer.addTo(this.map);
        this.debug_log("✅ [Map.vue] Tile layer added");

        // Add event handlers for debugging and initialization
        this.map.on("load", () => {
          this.debug_log("🎉 [Map.vue] Map load event fired - map is ready");
          // Load data only after map is fully loaded
          setTimeout(() => {
            this.debug_log("📊 [Map.vue] Loading map data after map load");
            this.load_map_data();
          }, 200);
        });

        this.currentTileLayer.on("load", () => {
          this.debug_log("🎉 [Map.vue] Tile layer load event fired");
        });

        this.currentTileLayer.on("loading", () => {
          this.debug_log("📡 [Map.vue] Tile layer loading...");
        });

        // Skip clustering for now to simplify
        this.debug_log("⚠️ [Map.vue] Skipping clustering for debugging");

        // Force invalidate size
        setTimeout(() => {
          if (this.map) {
            this.debug_log("📏 [Map.vue] Invalidating map size");
            this.map.invalidateSize();
            this.debug_map_state();
          }
        }, 100);

        // Load data immediately after map is created (don't wait for 'load' event)
        setTimeout(() => {
          this.debug_log("📊 [Map.vue] Loading map data immediately");
          this.load_map_data();
        }, 300);

        this.debug_log("🎉 [Map.vue] Map initialization completed");
      } catch (error) {
        console.error("❌ [Map.vue] Map initialization failed:", error);
        console.error("❌ [Map.vue] Error stack:", error.stack);

        // Retry after delay
        setTimeout(() => {
          this.debug_log("🔄 [Map.vue] Retrying map initialization");
          this.init_map();
        }, 2000);
      }
    },

    destroy_map() {
      try {
        // Clear all markers first
        this.clear_markers();

        // Remove marker cluster if exists
        if (this.marker_cluster && this.map) {
          this.map.removeLayer(this.marker_cluster);
          this.marker_cluster = null;
        }

        // Remove current tile layer if exists
        if (this.currentTileLayer && this.map) {
          this.map.removeLayer(this.currentTileLayer);
          this.currentTileLayer = null;
        }

        // Finally remove map
        if (this.map) {
          this.map.off(); // Remove all event listeners
          this.map.remove();
          this.map = null;
        }

        this.debug_log("🧹 [Map.vue] Map destroyed successfully");
      } catch (error) {
        console.error("❌ [Map.vue] Error destroying map:", error);
        // Force clear even if error occurred
        this.map = null;
        this.marker_cluster = null;
        this.currentTileLayer = null;
        this.markers = [];
      }
    },

    load_map_data() {
      let v = this;
      v.loading_data = true;

      this.debug_log("📡 [Map.vue] Starting to load map data...");
      this.debug_log("📡 [Map.vue] URL:", "/appraisal/appraisal/map-data");

      get("/appraisal/appraisal/map-data")
        .then((data) => {
          this.debug_log("✅ [Map.vue] Map data loaded successfully:", data);
          v.appraisals = data;
          v.calculate_statistics();
          v.update_markers();
        })
        .catch((error) => {
          console.error("❌ [Map.vue] Error loading map data:", error);
          error_message(v.$t("error loading map data"));
        })
        .finally(() => {
          v.loading_data = false;
          this.debug_log("🏁 [Map.vue] Map data loading finished");
        });
    },

    refresh_map_data() {
      this.debug_log("🔄 [Map.vue] Manual refresh requested");
      this.clear_markers();
      this.load_map_data();
    },

    calculate_statistics() {
      let v = this;
      let filtered_data = v.get_filtered_appraisals();

      if (filtered_data.length === 0) {
        v.map_statistics = {
          total: 0,
          avg_price: 0,
          max_price: 0,
          min_price: 0,
        };
        return;
      }

      let prices = filtered_data.map((item) => parseFloat(item.price_per_sqm));

      v.map_statistics = {
        total: filtered_data.length,
        avg_price: prices.reduce((a, b) => a + b, 0) / prices.length,
        max_price: Math.max(...prices),
        min_price: Math.min(...prices),
      };
    },

    get_filtered_appraisals() {
      let v = this;
      let filtered = v.appraisals;

      switch (v.map_view_type) {
        case "active":
          filtered = v.appraisals.filter((item) => item.status === "active");
          break;
        case "recent":
          let thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          filtered = v.appraisals.filter(
            (item) => new Date(item.valuation_date) >= thirtyDaysAgo
          );
          break;
        default:
          filtered = v.appraisals;
      }

      return filtered.filter((item) => item.latitude && item.longitude);
    },

    update_markers() {
      let v = this;
      v.clear_markers();

      let filtered_data = v.get_filtered_appraisals();

      filtered_data.forEach((appraisal) => {
        v.add_marker(appraisal);
      });

      if (filtered_data.length > 0) {
        v.zoom_to_fit();
      }
    },

    add_marker(appraisal) {
      let v = this;

      // Create simple marker
      let marker = L.marker([appraisal.latitude, appraisal.longitude]);

      // Create popup content
      let popup_content = `
        <div style="min-width: 200px;">
          <h6 style="margin-bottom: 8px; font-weight: bold;">${
            appraisal.property_address
          }</h6>
          <p style="margin: 4px 0;"><strong>${v.$t(
            "property type"
          )}:</strong> ${
        appraisal.property_type
          ? appraisal.property_type.name
          : v.$t("not specified")
      }</p>
          <p style="margin: 4px 0;"><strong>${v.$t(
            "price per sqm"
          )}:</strong> ${v.format_currency(appraisal.price_per_sqm)}</p>
          <p style="margin: 4px 0;"><strong>${v.$t("total area")}:</strong> ${
        appraisal.total_area
      } m²</p>
          <p style="margin: 8px 0 4px 0;"><strong>${v.$t(
            "total value"
          )}:</strong> ${v.format_currency(
        appraisal.price_per_sqm * appraisal.total_area
      )}</p>
          <div style="margin-top: 10px; text-align: center;">
            <button onclick="window.viewAppraisalDetails('${
              appraisal.uuid
            }')" style="margin-right: 5px; padding: 4px 8px; background: #3273dc; color: white; border: none; border-radius: 3px; cursor: pointer;">
              ${v.$t("details")}
            </button>
            <button onclick="window.editAppraisal('${
              appraisal.uuid
            }')" style="padding: 4px 8px; background: #ffdd57; color: #363636; border: none; border-radius: 3px; cursor: pointer;">
              ${v.$t("edit")}
            </button>
          </div>
        </div>
      `;

      marker.bindPopup(popup_content);

      // Add click event to select property
      marker.on("click", function () {
        v.selected_property = appraisal;
      });

      // Add directly to map (no clustering for now)
      marker.addTo(v.map);

      v.markers.push(marker);
    },

    get_marker_color(price) {
      let v = this;
      let max_price = v.map_statistics.max_price;
      let min_price = v.map_statistics.min_price;
      let range = max_price - min_price;

      if (range === 0) return "#3273dc"; // Default blue

      let percentage = (price - min_price) / range;

      if (percentage >= 0.8) return "#ff3860"; // Red (expensive)
      if (percentage >= 0.6) return "#ffdd57"; // Yellow (medium-high)
      if (percentage >= 0.4) return "#48c774"; // Green (medium)
      if (percentage >= 0.2) return "#3273dc"; // Blue (medium-low)
      return "#6c757d"; // Gray (low)
    },

    clear_markers() {
      let v = this;

      // Clear all markers from map
      v.markers.forEach((marker) => {
        if (v.map.hasLayer(marker)) {
          v.map.removeLayer(marker);
        }
      });

      v.markers = [];
      this.debug_log("🧹 [Map.vue] Markers cleared");
    },

    update_map_view() {
      this.calculate_statistics();
      this.update_markers();
    },

    toggle_clustering() {
      let v = this;

      if (v.enable_clustering) {
        if (window.L && window.L.markerClusterGroup) {
          v.marker_cluster = L.markerClusterGroup({
            chunkedLoading: true,
            chunkInterval: 200,
            chunkDelay: 50,
          });
          v.map.addLayer(v.marker_cluster);
        }
      } else {
        if (v.marker_cluster) {
          v.map.removeLayer(v.marker_cluster);
          v.marker_cluster = null;
        }
      }

      v.update_markers();
    },

    toggle_price_labels() {
      // This could be implemented to show/hide price labels on markers
      // For now, just update markers
      this.update_markers();
    },

    zoom_to_fit() {
      let v = this;

      if (v.markers.length === 0) return;

      if (!v.is_map_ready()) {
        this.debug_log("⚠️ [Map.vue] Map not ready for zoom_to_fit");
        return;
      }

      let group = new L.featureGroup(v.markers);
      v.map.fitBounds(group.getBounds().pad(0.1));
    },

    reset_map_view() {
      let v = this;
      if (!v.is_map_ready()) {
        this.debug_log("⚠️ [Map.vue] Map not ready for reset_map_view");
        return;
      }
      v.map.setView([14.6349, -90.5069], 10);
    },

    view_property_details(uuid) {
      this.$refs.appraisal_detail_modal.show_modal(uuid);
    },

    edit_property(uuid) {
      this.$refs.appraisal_modal.show_modal(uuid);
    },

    on_appraisal_updated() {
      this.refresh_map_data();
      this.selected_property = null;
    },

    search_location() {
      let v = this;

      if (!v.search_query.trim() || v.search_query.length < 3) {
        return;
      }

      v.searching_location = true;

      // Prepare search query with Guatemala context
      let query = v.search_query.trim();
      if (!query.toLowerCase().includes("guatemala")) {
        query += ", Guatemala";
      }

      // Use Nominatim geocoding service (OpenStreetMap's geocoding)
      const encodedQuery = encodeURIComponent(query);
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodedQuery}&limit=5&countrycodes=gt&addressdetails=1&extratags=1`;

      fetch(url)
        .then((response) => response.json())
        .then((data) => {
          v.searching_location = false;

          if (data && data.length > 0) {
            // Get the best result (first one is usually the most relevant)
            const result = data[0];
            const lat = parseFloat(result.lat);
            const lng = parseFloat(result.lon);

            // Validate coordinates are within Guatemala bounds (approximately)
            if (lat >= 13.0 && lat <= 18.0 && lng >= -92.5 && lng <= -88.0) {
              // Determine appropriate zoom level based on place type
              let zoomLevel = 15; // Default zoom

              if (result.type === "city" || result.type === "town") {
                zoomLevel = 12;
              } else if (result.type === "village") {
                zoomLevel = 14;
              } else if (
                result.type === "house" ||
                result.type === "building"
              ) {
                zoomLevel = 18;
              }

              // Move map to location
              if (v.is_map_ready()) {
                v.map.setView([lat, lng], zoomLevel);

                // Add a temporary marker for the searched location
                let searchMarker = L.marker([lat, lng], {
                  icon: L.icon({
                    iconUrl:
                      "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
                    shadowUrl:
                      "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
                    iconSize: [25, 41],
                    iconAnchor: [12, 41],
                    popupAnchor: [1, -34],
                    shadowSize: [41, 41],
                  }),
                }).addTo(v.map);

                // Add popup with search result info
                let addressParts = [];
                if (result.address) {
                  if (result.address.house_number)
                    addressParts.push(result.address.house_number);
                  if (result.address.road)
                    addressParts.push(result.address.road);
                  if (result.address.neighbourhood)
                    addressParts.push(result.address.neighbourhood);
                  if (
                    result.address.city ||
                    result.address.town ||
                    result.address.village
                  ) {
                    addressParts.push(
                      result.address.city ||
                        result.address.town ||
                        result.address.village
                    );
                  }
                  if (result.address.state)
                    addressParts.push(result.address.state);
                }

                let displayAddress =
                  addressParts.length > 0
                    ? addressParts.join(", ")
                    : result.display_name;

                searchMarker
                  .bindPopup(
                    `
                  <div>
                    <h6><strong>${v.$t("searched location")}</strong></h6>
                    <p>${displayAddress}</p>
                    <p><small>Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(
                      6
                    )}</small></p>
                  </div>
                `
                  )
                  .openPopup();

                // Remove the search marker after 10 seconds
                setTimeout(() => {
                  v.map.removeLayer(searchMarker);
                }, 10000);
              }

              // Clear search query after successful search
              v.search_query = "";

              // Show success message
              success_message(v.$t("location found and set"));
            } else {
              warning_message(v.$t("location outside guatemala"));
            }
          } else {
            error_message(v.$t("location not found"));
          }
        })
        .catch((error) => {
          v.searching_location = false;
          console.error("Geocoding error:", error);
          error_message(v.$t("error searching location"));
        });
    },

    center_map_guatemala() {
      let v = this;
      if (!v.is_map_ready()) {
        this.debug_log("⚠️ [Map.vue] Map not ready for center_map_guatemala");
        return;
      }
      v.map.setView([14.6349, -90.5069], 8);
    },

    fit_all_markers() {
      let v = this;
      if (v.markers.length === 0) {
        warning_message(v.$t("no markers to fit"));
        return;
      }

      if (!v.is_map_ready()) {
        this.debug_log("⚠️ [Map.vue] Map not ready for fit_all_markers");
        return;
      }

      let group = new L.featureGroup(v.markers);
      v.map.fitBounds(group.getBounds().pad(0.1));
    },

    force_map_refresh() {
      let v = this;
      if (!v.is_map_ready()) {
        this.debug_log("⚠️ [Map.vue] Map not ready for force_map_refresh");
        return;
      }

      this.debug_log("Forcing map refresh...");

      // 1. Multiple invalidate size calls
      v.map.invalidateSize();
      setTimeout(() => v.map.invalidateSize(), 50);
      setTimeout(() => v.map.invalidateSize(), 150);

      // 2. Force redraw of all layers
      v.map.eachLayer(function (layer) {
        if (layer.redraw) {
          layer.redraw();
        }
        // Force tile layer refresh
        if (layer._tiles) {
          Object.keys(layer._tiles).forEach((key) => {
            let tile = layer._tiles[key];
            if (tile.el) {
              tile.el.style.display = "none";
              setTimeout(() => {
                tile.el.style.display = "block";
              }, 10);
            }
          });
        }
      });

      // 3. Aggressive pan sequence to force tile reload
      let center = v.map.getCenter();
      let zoom = v.map.getZoom();

      // Pan in different directions
      v.map.panTo([center.lat + 0.001, center.lng + 0.001]);
      setTimeout(() => {
        v.map.panTo([center.lat - 0.001, center.lng - 0.001]);
      }, 100);
      setTimeout(() => {
        v.map.panTo(center);
      }, 200);

      // 4. Zoom out and back in to force tile reload
      setTimeout(() => {
        v.map.setZoom(zoom - 1);
        setTimeout(() => {
          v.map.setZoom(zoom);
        }, 150);
      }, 300);

      // 5. Force container resize
      setTimeout(() => {
        let container = v.$refs.mapContainer;
        if (container) {
          container.style.height = container.offsetHeight - 1 + "px";
          setTimeout(() => {
            container.style.height = "";
            v.map.invalidateSize();
          }, 50);
        }
      }, 500);

      // 6. Final invalidation
      setTimeout(() => {
        v.map.invalidateSize();
        v.debug_log("Map refresh completed");
      }, 800);
    },

    switch_tile_provider() {
      let v = this;
      if (v.map && v.currentTileLayer) {
        // Remove current tile layer
        v.map.removeLayer(v.currentTileLayer);

        // Add alternative tile provider (CartoDB)
        v.currentTileLayer = L.tileLayer(
          "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
          {
            attribution: "© OpenStreetMap contributors © CARTO",
            maxZoom: 19,
            updateWhenIdle: false,
            updateWhenZooming: true,
            keepBuffer: 2,
          }
        );

        v.currentTileLayer.addTo(v.map);

        // Force refresh after switching
        setTimeout(() => {
          v.force_map_refresh();
        }, 200);
      }
    },

    format_currency,

    // Debug method
    debug_map_state() {
      this.debug_log("🔍 [Debug] Map state check:");
      this.debug_log("- Leaflet available:", typeof L !== "undefined");
      this.debug_log(
        "- Container exists:",
        !!document.getElementById("main-map")
      );
      this.debug_log("- Map object:", !!this.map);
      this.debug_log("- Container dimensions:", {
        width: document.getElementById("main-map")?.offsetWidth,
        height: document.getElementById("main-map")?.offsetHeight,
      });

      const container = document.getElementById("main-map");
      if (container) {
        this.debug_log("- Container styles:", {
          display: getComputedStyle(container).display,
          visibility: getComputedStyle(container).visibility,
          height: getComputedStyle(container).height,
          width: getComputedStyle(container).width,
        });
      }
    },
  },
  created() {
    // Global functions for popup buttons
    window.viewAppraisalDetails = (uuid) => {
      this.view_property_details(uuid);
    };

    window.editAppraisal = (uuid) => {
      this.edit_property(uuid);
    };
  },
};
</script>

<style scoped>
.appraisal-map {
  padding: 0;
}

.title {
  margin-bottom: 0;
}

.level {
  margin-bottom: 1.5rem;
}

.box {
  border-radius: 6px;
}

#main-map {
  border-radius: 6px;
  overflow: hidden;
  height: 70vh !important;
  width: 100% !important;
  position: relative;
  z-index: 1;
  background: #f5f5f5;
}

/* Ensure Leaflet styles are properly applied */
#main-map .leaflet-container {
  height: 100% !important;
  width: 100% !important;
  background: #a6cee3;
}

#main-map .leaflet-tile {
  max-width: none !important;
  max-height: none !important;
}

#main-map .leaflet-control-container {
  z-index: 1000;
}

.custom-marker {
  background: transparent;
  border: none;
}

.modal-content .box {
  max-width: 300px;
  margin: 0 auto;
}

.columns.mb-4 {
  margin-bottom: 1.5rem !important;
}

.heading {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #b5b5b5;
}
</style>
